import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Code, Cpu, Database, Layers, Zap, Settings } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const moduleIcons = [Code, Zap, Database, Layers, Settings, Cpu, Code, Zap, Database, Layers];

const CppCurriculum = () => {
  const { language } = useLanguage();
  const [expandedModules, setExpandedModules] = useState(new Set([0]));
  
  const toggleModule = (moduleIndex) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleIndex)) {
      newExpanded.delete(moduleIndex);
    } else {
      newExpanded.add(moduleIndex);
    }
    setExpandedModules(newExpanded);
  };

  const curriculum = [
  {
    title: language === 'ar' ? 'الأساسيات 1' : 'Basics 1',
    lessons: [
      language === 'ar' ? 'تثبيت بيئة التطوير (IDE)' : 'Install IDE',
      language === 'ar' ? 'بنية لغة ++C' : 'C++ Syntax',
      language === 'ar' ? 'الدالة print والسطر الجديد \\n و std::' : 'Output (print) + \\n + std',
      language === 'ar' ? 'التعليقات' : 'Comments',
      language === 'ar' ? '\\n مقابل endl' : '\\n vs endl',
      language === 'ar' ? 'محارف الهروب (Escape sequences)' : 'Escape Sequences',
      language === 'ar' ? 'المتغيرات' : 'Variables',
      language === 'ar' ? 'إدخال المستخدم' : 'User Input'
    ]
  },
  {
    title: language === 'ar' ? 'العوامل والرياضيات' : 'Operators & Math',
    lessons: [
      language === 'ar' ? 'العوامل الحسابية' : 'Arithmetic Operators',
      language === 'ar' ? 'عامل الزيادة/النقصان ++ و --' : 'Increment / Decrement (++ / --)',
      language === 'ar' ? 'x++ و ++x ، x-- و --x' : 'x++ vs ++x, x-- vs --x',
      language === 'ar' ? 'عوامل الإسناد' : 'Assignment Operators',
      language === 'ar' ? 'العوامل العلائقية' : 'Relational Operators',
      language === 'ar' ? 'العوامل المنطقية AND / OR / NOT' : 'Logical Operators (AND / OR / NOT)',
      language === 'ar' ? 'دوال الرياضيات' : 'Math Functions'
    ]
  },
  {
    title: language === 'ar' ? 'التركيبات والتعدادات' : 'Structs & Enums',
    lessons: [
      language === 'ar' ? 'التركيبات (struct)' : 'Structures',
      language === 'ar' ? 'التعدادات (enum)' : 'Enums',
      language === 'ar' ? 'دمج enum مع struct' : 'Enums + Structs',
      language === 'ar' ? 'تحويل أنواع البيانات' : 'Data Type Conversion',
      language === 'ar' ? 'السلاسل النصية' : 'Strings'
    ]
  },
  {
    title: language === 'ar' ? 'الدوال — الجزء 1' : 'Functions — Part 1',
    lessons: [
      language === 'ar' ? 'تعريف الدوال واستدعاؤها' : 'Defining & Calling Functions',
      language === 'ar' ? 'المعاملات وقيم الإرجاع' : 'Parameters & Return Values'
    ]
  },
  {
    title: language === 'ar' ? 'الدوال — الجزء 2' : 'Functions — Part 2',
    lessons: [
      language === 'ar' ? 'نطاق المتغيرات' : 'Variable Scope',
      language === 'ar' ? 'التمرير بالقيمة والمرجع' : 'Pass-by-Value vs Pass-by-Reference',
      language === 'ar' ? 'دالة داخل دالة (سلاسل الاستدعاء)' : 'Function in Function (Call Chains)'
    ]
  },
  {
    title: language === 'ar' ? 'التركيبات والمصفوفات' : 'Structs & Arrays',
    lessons: [
      language === 'ar' ? 'التركيبات مع الدوال' : 'Structures with Functions',
      language === 'ar' ? 'المصفوفات' : 'Arrays',
      language === 'ar' ? 'المصفوفات مع الدوال' : 'Arrays with Functions',
      language === 'ar' ? 'مصفوفات من التركيبات' : 'Arrays of Structs'
    ]
  },
  {
    title: language === 'ar' ? 'التفرع (التحكم بالمسار)' : 'Branching',
    lessons: [
      language === 'ar' ? 'if و if…else' : 'if / if–else',
      language === 'ar' ? 'استخدام enum مع if' : 'Enums with if',
      language === 'ar' ? 'التبديل switch' : 'switch'
    ]
  },
  {
    title: language === 'ar' ? 'الحلقات' : 'Loops',
    lessons: [
      language === 'ar' ? 'حلقة for' : 'for loop',
      language === 'ar' ? 'for مع المصفوفات' : 'for loop with arrays',
      language === 'ar' ? 'حلقة for داخل for' : 'Nested for (for in for)',
      language === 'ar' ? 'حلقة while' : 'while loop',
      language === 'ar' ? 'حلقة do…while' : 'do…while loop',
      language === 'ar' ? 'تعليمة الإيقاف break' : 'break statement',
      language === 'ar' ? 'حلقة while (مراجعة وتطبيقات)' : 'while (practice & patterns)'
    ]
  }
  // ملاحظة: إذا كنت تريد 28 درساً منفصلة بدلاً من 8 وحدات،
  // يمكنك تحويل كل عنصر في هذه القائمة إلى درس منفصل
  // بوضع عنوان الدرس في "title" وموضوعاته في "lessons".
];

  
  return (
    <section id="curriculum" className="cpp-section">
      <div className="cpp-container">
        <div className="text-center mb-12">
          <h2 className="cpp-heading-1 mb-4">
            {language === 'ar' ? 'منهج C++ الكامل' : 'Complete C++ Curriculum'}
          </h2>
          <p className="cpp-body-large max-w-3xl mx-auto">
            {language === 'ar' 
              ? 'برنامجنا الشامل لمدة 10 أسابيع ينقلك من مبتدئ كامل إلى مبرمج C++ واثق. كل وحدة تبني على السابقة.'
              : 'Our comprehensive 10-week program takes you from complete beginner to confident C++ programmer. Each module builds on the previous one.'
            }
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto">
          {curriculum.map((module, index) => {
            const IconComponent = moduleIcons[index] || Code;
            const isExpanded = expandedModules.has(index);
            
            return (
              <div key={index} className="cpp-card mb-6 hover:transform hover:scale-[1.02] transition-all duration-300">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleModule(index)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <div className="text-blue-400 text-sm font-mono mb-1">
                        {language === 'ar' ? `الوحدة ${index + 1}` : `MODULE ${index + 1}`}
                      </div>
                      <h3 className="cpp-heading-3 text-white">{module.title}</h3>
                      <div className="text-gray-400 text-sm mt-1">
                        {module.lessons.length} {language === 'ar' ? 'دروس' : 'lessons'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-gray-400 text-sm hidden sm:block">
                      {language === 'ar' 
                        ? (isExpanded ? 'انقر للطي' : 'انقر للتوسيع')
                        : `Click to ${isExpanded ? 'collapse' : 'expand'}`
                      }
                    </div>
                    {isExpanded ? 
                      <ChevronUp className="w-6 h-6 text-blue-400" /> : 
                      <ChevronDown className="w-6 h-6 text-blue-400" />
                    }
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="mt-8 pt-6 border-t border-gray-700">
                    <div className="grid md:grid-cols-2 gap-4">
                      {module.lessons.map((lesson, lessonIndex) => (
                        <div key={lessonIndex} className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors">
                          <div className="w-3 h-3 bg-green-400 rounded-full flex-shrink-0"></div>
                          <span className="text-gray-300 text-sm font-mono">{lesson}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Problem Solving Preview */}
                    <div className="mt-6 p-4 bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-lg border border-blue-500/20">
                      <h4 className="text-blue-400 font-semibold mb-2">
                        🧩 {language === 'ar' ? `حل المشاكل - الوحدة ${index + 1}` : `Module ${index + 1} Problem Solving`}
                      </h4>
                      <p className="text-gray-400 text-sm">
                        {language === 'ar'
                          ? 'تمارين وتطبيقات برمجية لتعزيز المفاهيم المتعلمة في هذه الوحدة. ابن برامج حقيقية خطوة بخطوة!'
                          : 'Practice problems and coding exercises to reinforce the concepts learned in this module. Build real programs step by step!'
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Learning Philosophy */}
        <div className="mt-16 text-center">
          <div className="cpp-card max-w-3xl mx-auto bg-gradient-to-r from-purple-900/20 to-blue-900/20 border-purple-500/30">
            <h3 className="cpp-heading-2 text-white mb-6">
              {language === 'ar' ? '🧱 نهج التعلم المتدرج' : '🧱 Step-by-Step Learning Approach'}
            </h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="w-16 h-16 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🟦</span>
                </div>
                <h4 className="text-blue-400 font-semibold mb-2">
                  {language === 'ar' ? 'بناء الأساس' : 'Build Foundation'}
                </h4>
                <p className="text-gray-400 text-sm">
                  {language === 'ar' 
                    ? 'ابدأ بالأساسيات - بناء الجملة والمتغيرات والعمليات البسيطة'
                    : 'Start with basic blocks - syntax, variables, and simple operations'
                  }
                </p>
              </div>
              
              <div>
                <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🟩</span>
                </div>
                <h4 className="text-green-400 font-semibold mb-2">
                  {language === 'ar' ? 'ربط المفاهيم' : 'Stack Concepts'}
                </h4>
                <p className="text-gray-400 text-sm">
                  {language === 'ar' 
                    ? 'كل مفهوم جديد يرتبط تماماً بما تعلمته بالفعل'
                    : 'Each new concept connects perfectly to what you already know'
                  }
                </p>
              </div>
              
              <div>
                <div className="w-16 h-16 bg-purple-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🟪</span>
                </div>
                <h4 className="text-purple-400 font-semibold mb-2">
                  {language === 'ar' ? 'حل المشكلات' : 'Problem Solving'}
                </h4>
                <p className="text-gray-400 text-sm">
                  {language === 'ar' 
                    ? 'تعلم طريقة التفكير وحل المشكلات الواقعية خطوة بخطوة'
                    : 'Learn how to think critically and solve real-world problems step by step.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CppCurriculum;
